import {
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  Switch,
  Typography,
  MenuItem,
  RadioGroup,
  IconButton,
  Box,
  Checkbox,
} from "@mui/material";
import { Field } from "formik";
import {
  DiscountDateRangeValue,
  EditClientInvoiceRouteRateUIRequest,
} from "../ClientInvoiceInterfaces";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { DISOUNT_RATE_TYPES } from "../../ratecard/RateCardConstants";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";
import DatePicker from "react-multi-date-picker";
import {
  isDisabledDate,
  isDisabledFieldDueToDateRangeEdits,
  mapFrequencyTextToNumber,
} from "../utils/RatecardUtils";
import AddIcon from "@mui/icons-material/Add";
import { formatDate } from "../../../../components/common/utils/DateUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ClientInvoiceEditDetailResponse,
  RouteResponse,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import { RatecardResponse } from "../../ratecard/dtos/RateCardResponse";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/features/store/store";
import { useEffect, useState } from "react";
import InfoWithTooltip from "../InfoWithToolTip";

interface Props {
  values: EditClientInvoiceRouteRateUIRequest;
  handleChange: any;
  handleBlur: any;
  setFieldValue: any;
  invoiceStartDate: Date;
  invoiceEndDate: Date;
  route: RouteResponse | null;
  originalRatecardData: RatecardResponse | undefined;
  store: string;
  editData: ClientInvoiceEditDetailResponse | null;
}
const CiDiscountPremium = (props: Props) => {
  const { values, handleChange, handleBlur, setFieldValue } = props;

  const disableFieldDateRanges = () => {
    if (
      props.values.master_date_range === undefined ||
      (props.values.master_date_range &&
        props.values.master_date_range.length < 2)
    )
      return false;
    return true;
  };

  const resetFieldAndDateRange = (
    fieldName: string,
    dateRangeFieldName: string
  ) => {
    // props.setFieldValue(fieldName, props.resetValue);
    setFieldValue(`show_discount`, false);
    setFieldValue(dateRangeFieldName, []);
  };

  const getUsedDateRanges = (dateRangesValues: DiscountDateRangeValue[]) => {
    return dateRangesValues.map((item) => {
      // item.date_range.map((dateObject) => dateObject.toDate())

      let startDate = item.startDate;
      let endDate = item.endDate;
      // this if is used for special case where we fetch date range edits and get them as string
      // as that created a type issue so I converted that to Date object right here
      if (typeof startDate === "string") startDate = new Date(startDate);
      if (typeof endDate === "string") endDate = new Date(endDate);
      return [startDate, endDate];
    });
  };

  const [tooltipText, setTooltipText] = useState<string>("");
  const { storesForCurrentClientInvoice } = useSelector(
    (state: RootState) => state.clientInvoice
  );
  useEffect(() => {
    // Extract user-selected dates from either master date range or discount date range
    const userStartDate = new Date(
      values?.master_date_range?.length
        ? values.master_date_range[0]
        : values?.discount_date_range[0]?.toDate()
    );
    const userEndDate = new Date(
      values?.master_date_range?.length
        ? values.master_date_range[1]
        : values?.discount_date_range[1]?.toDate()
    );

    // Function to check if the user-selected dates collide with a given existing date range
    const isDateRangeColliding = (
      existingStart: Date,
      existingEnd: Date
    ): boolean => {
      return userStartDate <= existingEnd && userEndDate >= existingStart;
    };

    // Check for collisions with all existing ranges and collect conflicting store names
    const conflictingStoreNames = (
      values?.discount_date_range_values || []
    ).reduce(
      (acc, existingRange) => {
        const existingStartDate = new Date(existingRange.startDate);
        const existingEndDate = new Date(existingRange.endDate);

        // Check if there's a collision with the user-selected date range
        if (isDateRangeColliding(existingStartDate, existingEndDate)) {
          const existingStoreIds = existingRange?.storeIds;
          const conflictingStores = props.store
            ?.split(",")
            .map(Number)
            .filter((storeId) => existingStoreIds?.includes(storeId))
            .map(
              (storeId) =>
                storesForCurrentClientInvoice.find(
                  (store) => store.id === storeId
                )?.storeId
            )
            .filter(Boolean) as string[];

          acc.push(...conflictingStores); // Accumulate conflicting store names
        }

        return acc;
      },
      [] as string[] // Start with an empty array for accumulating conflicting store names
    );

    // Create a warning message if there are any conflicting stores
    const warnings =
      conflictingStoreNames.length > 0
        ? `This date range edit will not work for the stores mentioned below as they have conflicting date ranges:\n\n${conflictingStoreNames
            .map((storeName) => `- ${storeName}`)
            .join("\n")}`
        : "";

    // Set tooltip text with warnings, if any
    setTooltipText(warnings);
  }, [values?.discount_date_range, values?.master_date_range]);

  const isMultiStoreEdit = (store: string): boolean => {
    return store?.split(",").map(Number).length > 1;
  };

  return (
    <>
      <Grid container alignItems={"flex-start"}>
<Grid item>
  <FormControlLabel
    id="show_discount"
    name="show_discount"
    label="Discount Premium"
    sx={{ mt: 2, mr: 2 }}
    labelPlacement="start"
    disabled={
      values.master_date_range && values.master_date_range.length === 2
        ? isDisabledFieldDueToDateRangeEdits(
            values.master_date_range[0],
            values.master_date_range[1],
            getUsedDateRanges(values.fuel_surcharge_date_range_values)
          )
        : false
    }
    control={
      <Field name="show_discount">
        {({ field, form }: any) => (
          <Checkbox
            {...field}
            checked={field.value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              form.setFieldValue(field.name, checked);
            }}
          />
        )}
      </Field>
    }
  />
        </Grid>
        <Grid item xs={1}>
          <InfoWithTooltip
            tooltipText={tooltipText}
            isDisabled={tooltipText === "" ? true : false}
            sx={{ color: "orange" }} // Set icon color to orange
          />
        </Grid>
        {props.route === null ? (
          <Grid item xs={5} ml={2} display={values.show_discount ? "" : "none"}>
            <DatePicker
              format="MM/DD/YYYY"
              className="green"
              dateSeparator=" ~ "
              range
              rangeHover
              placeholder="Date Range"
              style={{ height: "36px", marginTop: "8px" }}
              minDate={props.invoiceStartDate}
              maxDate={(() => {
                const adjustedEndDate = new Date(props.invoiceEndDate);
                adjustedEndDate.setHours(23, 59, 59, 999);
                return adjustedEndDate;
              })()}
              disabled={disableFieldDateRanges()}
              value={values.discount_date_range}
              onChange={(value) => {
                setFieldValue(`discount_date_range`, value);
              }}
              mapDays={({ date }) => {
                let localProps: any = {};
                // Proceed with disabling logic only if not in multi-store edit mode
                if (!isMultiStoreEdit(props?.store)) {
                  const usedDateRanges = values.discount_date_range_values.map(
                    (item) => {
                      let startDate =
                        typeof item.startDate === "string"
                          ? new Date(item.startDate)
                          : item.startDate;
                      let endDate =
                        typeof item.endDate === "string"
                          ? new Date(item.endDate)
                          : item.endDate;
                      return [startDate, endDate];
                    }
                  );

                  // Disable date if it falls within any of the used date ranges
                  if (isDisabledDate(date.toDate(), usedDateRanges)) {
                    localProps.disabled = true;
                    localProps.style = { color: "gray", pointerEvents: "none" };
                  }
                }

                return localProps;
              }}
            />

            <IconButton
              color="success"
              disabled={
                disableFieldDateRanges() ||
                (values.discount_date_range &&
                  values.discount_date_range.length < 2)
              }
              onClick={() => {
                if (
                  // props.field !== undefined &&
                  values.discount_date_range.length === 2
                ) {
                  let dateRangeValues = values.discount_date_range_values;
                  let discountDateRangeValue: DiscountDateRangeValue = {
                    startDate: values.discount_date_range[0].toDate(),
                    endDate: values.discount_date_range[1].toDate(),
                  };

                  if (values.discount_custom_value)
                    discountDateRangeValue.discountCustomValue =
                      values.discount_custom_value;
                  else {
                    discountDateRangeValue.discountType =
                      values.discount_type === "volume"
                        ? "volume"
                        : values.discount_rate_type;
                    // handle volume discount
                    if (values.discount_type === "volume") {
                      discountDateRangeValue.discountMinVolumeRate =
                        values.discount_volume_min_unit;
                      discountDateRangeValue.discountFrequency =
                        mapFrequencyTextToNumber(
                          `${values.discount_volume_frequency}`
                        );
                      if (values.discount_volume_unit_type === "percentage") {
                        discountDateRangeValue.discountUnitType = "percentage";
                        discountDateRangeValue.discountValue =
                          values.discount_volume_value_percentage;
                      } else {
                        discountDateRangeValue.discountUnitType = "value";
                        discountDateRangeValue.discountValue =
                          values.discount_volume_value_dollar;
                      }
                    }
                    // handle rate discount
                    else if (values.discount_type === "rate") {
                      discountDateRangeValue.discountMinVolumeRate =
                        values.discount_rate_min_unit;

                      discountDateRangeValue.discountFrequency =
                        mapFrequencyTextToNumber(
                          `${values.discount_rate_frequency}`
                        );
                      if (values.discount_rate_unit_type === "percentage") {
                        discountDateRangeValue.discountUnitType = "percentage";
                        discountDateRangeValue.discountValue =
                          values.discount_rate_value_percentage;
                      } else {
                        discountDateRangeValue.discountUnitType = "value";
                        discountDateRangeValue.discountValue =
                          values.discount_rate_value_dollar;
                      }
                    }
                  }

                  dateRangeValues.push(discountDateRangeValue);
                  setFieldValue(`discount_date_range_values`, dateRangeValues);

                  resetFieldAndDateRange(
                    `props.fieldName`,
                    `discount_date_range`
                  );
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Grid>
        ) : null}
      </Grid>
      <Grid container item alignItems={"center"}>
        <Grid item display={values.show_discount ? "" : "none"}>
          <FormikTextField
            id="discount_custom_value"
            name="discount_custom_value"
            variant="outlined"
            size="small"
            label="Add Custom Value"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid container item alignItems={"center"}>
        <RadioGroup
          row
          id="discount_type"
          name="discount_type"
          value={values.discount_type}
          onChange={handleChange}
          onBlur={handleBlur}
          sx={{ display: values.show_discount ? "block" : "none" }}
        >
          {/* First Discount statement */}
          <FormControlLabel
            control={<Radio />}
            disabled={values.discount_custom_value ? true : false}
            value="volume"
            label={
              <Grid container item alignItems={"center"} spacing={1}>
                <Grid item>
                  <Typography>If volume greater than </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    margin="normal"
                    id="discount_volume_min_unit"
                    name="discount_volume_min_unit"
                    label=""
                    placeholder="Units"
                    size="small"
                    isDisabled={values.discount_custom_value ? true : false}
                    variant="outlined"
                    sx={{ width: 70 }}
                  />
                </Grid>
                <Grid item>
                  <Typography>over </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_volume_frequency"
                    name="discount_volume_frequency"
                    label="Frequency"
                    size="small"
                    isDisabled={values.discount_custom_value ? true : false}
                    sx={{ width: 110 }}
                    variant="outlined"
                  >
                    {["weekly", "bi-weekly", "monthly"].map(
                      (timePeriod, index) => (
                        <MenuItem key={index} value={timePeriod}>
                          {timePeriod}
                        </MenuItem>
                      )
                    )}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>then discount equals to </Typography>
                </Grid>
                <Grid item>
                  <RadioGroup
                    row
                    id="discount_volume_unit_type"
                    name="discount_volume_unit_type"
                    value={values.discount_volume_unit_type}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      control={<Radio />}
                      disabled={values.discount_custom_value ? true : false}
                      value="percentage"
                      label={
                        <FormikTextField
                          id="discount_volume_value_percentage"
                          name="discount_volume_value_percentage"
                          variant="outlined"
                          isDisabled={
                            values.discount_custom_value ? true : false
                          }
                          size="small"
                          label=""
                          sx={{ width: 80 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                %
                              </InputAdornment>
                            ),
                          }}
                        />
                      }
                    />
                    <FormControlLabel
                      control={<Radio />}
                      value="value"
                      disabled={values.discount_custom_value ? true : false}
                      label={
                        <FormikTextField
                          id="discount_volume_value_dollar"
                          name="discount_volume_value_dollar"
                          variant="outlined"
                          isDisabled={
                            values.discount_custom_value ? true : false
                          }
                          size="small"
                          label=""
                          sx={{ width: 80 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      }
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            }
          />
          {/* Second Discount statement */}
          <FormControlLabel
            control={<Radio />}
            disabled={values.discount_custom_value ? true : false}
            value="rate"
            label={
              <Grid container item alignItems={"center"} spacing={1}>
                <Grid item>
                  <Typography>If </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_rate_type"
                    name="discount_rate_type"
                    label="Rate Type"
                    isDisabled={values.discount_custom_value ? true : false}
                    size="small"
                    sx={{ width: 140 }}
                    variant="outlined"
                  >
                    {DISOUNT_RATE_TYPES.map((rateType, index) => (
                      <MenuItem
                        key={index}
                        value={rateType}
                        style={{ textTransform: "capitalize" }}
                      >
                        {mapSnakeCaseToCapitalCaseWithSpaces(rateType)}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>greater than </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    margin="normal"
                    id="discount_rate_min_unit"
                    name="discount_rate_min_unit"
                    label=""
                    placeholder="Dollars"
                    isDisabled={values.discount_custom_value ? true : false}
                    size="small"
                    variant="outlined"
                    sx={{ width: 70 }}
                  />
                </Grid>
                <Grid item>
                  <Typography>over </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_rate_frequency"
                    name="discount_rate_frequency"
                    label="Frequency"
                    size="small"
                    isDisabled={values.discount_custom_value ? true : false}
                    sx={{ width: 110 }}
                    variant="outlined"
                  >
                    {["weekly", "monthly"].map((timePeriod, index) => (
                      <MenuItem key={index} value={timePeriod}>
                        {timePeriod}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>then discount equals to </Typography>
                </Grid>
                <Grid item>
                  <RadioGroup
                    row
                    id="discount_rate_unit_type"
                    name="discount_rate_unit_type"
                    value={values.discount_rate_unit_type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <FormControlLabel
                      control={<Radio />}
                      value="percentage"
                      disabled={values.discount_custom_value ? true : false}
                      label={
                        <FormikTextField
                          id="discount_rate_value_percentage"
                          name="discount_rate_value_percentage"
                          variant="outlined"
                          label=""
                          isDisabled={
                            values.discount_custom_value ? true : false
                          }
                          size="small"
                          sx={{ width: 80 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                %
                              </InputAdornment>
                            ),
                          }}
                        />
                      }
                    />
                    <FormControlLabel
                      control={<Radio />}
                      value="value"
                      disabled={values.discount_custom_value ? true : false}
                      label={
                        <FormikTextField
                          id="discount_rate_value_dollar"
                          name="discount_rate_value_dollar"
                          variant="outlined"
                          size="small"
                          isDisabled={
                            values.discount_custom_value ? true : false
                          }
                          label=""
                          sx={{ width: 80 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      }
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            }
          />
        </RadioGroup>
      </Grid>
      {/* Date range edits for Discount */}
      {props.route === null ? (
        <Grid container item xs={12} sm={12} md={12} lg={12}>
          {values.discount_date_range_values.map((item, index) => {
            return (
              <Box display="block" width="100%">
                <Typography variant="overline">
                  <>
                    Date Range: {formatDate(item.startDate)} to{" "}
                    {formatDate(item.endDate)}
                  </>
                </Typography>
                {item.discountCustomValue ? (
                  <Typography variant="overline">
                    <>, Custom Value: {item.discountCustomValue}</>
                  </Typography>
                ) : null}
                {!item.discountCustomValue && item.discountType === "volume" ? (
                  <Typography variant="overline">
                    <>
                      , If volume greater than {item.discountMinVolumeRate} over{" "}
                      {item.discountFrequency} days then discount equals to{" "}
                      {item.discountUnitType === "value" ? `$` : ``}{" "}
                      {item.discountValue}
                      {item.discountUnitType === "percentage" ? `%` : ``}
                    </>
                  </Typography>
                ) : null}
                {!item.discountCustomValue && item.discountType !== "volume" ? (
                  <Typography variant="overline">
                    <>
                      , If {item.discountType} greater than $
                      {item.discountMinVolumeRate} over {item.discountFrequency}{" "}
                      days then discount equals to{" "}
                      {item.discountUnitType === "value" ? `$` : ``}{" "}
                      {item.discountValue}
                      {item.discountUnitType === "percentage" ? `%` : ``}
                    </>
                  </Typography>
                ) : null}
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    const dateRangeValues =
                      values.discount_date_range_values.filter(
                        (item, idx) => idx !== index
                      );

                    setFieldValue(
                      `discount_date_range_values`,
                      dateRangeValues
                    );
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}
        </Grid>
      ) : null}
      {/* Original Discount Values */}
      {props.route !== null &&
      (props.editData?.ratecardValues.discountType ||
        props.editData?.discountCustomValue) ? (
        <Grid container item xs={12} sm={12} md={12} lg={12}>
          <Box display="block" width="100%">
            {/* Not adding custom value here as this will only be used to show original ratecard data, and original ratecard will not have any custom value */}
            {props.originalRatecardData?.ratecardValues.discountType ===
            "volume" ? (
              <Typography variant="overline" color={"rgb(0, 0, 0, 0.6);"}>
                <>
                  Original: If volume greater than{" "}
                  {
                    props.originalRatecardData?.ratecardValues
                      .discountMinVolumeRate
                  }{" "}
                  over{" "}
                  {props.originalRatecardData?.ratecardValues.discountFrequency}{" "}
                  days then discount equals to{" "}
                  {props.originalRatecardData?.ratecardValues
                    .discountUnitType === "value"
                    ? `$`
                    : ``}{" "}
                  {props.originalRatecardData?.ratecardValues.discountValue}
                  {props.originalRatecardData?.ratecardValues
                    .discountUnitType === "percentage"
                    ? `%`
                    : ``}
                </>
              </Typography>
            ) : null}
            {props.originalRatecardData?.ratecardValues.discountType !==
            "volume" ? (
              <Typography variant="overline">
                <>
                  Original: If{" "}
                  {props.originalRatecardData?.ratecardValues.discountType}{" "}
                  greater than $
                  {
                    props.originalRatecardData?.ratecardValues
                      .discountMinVolumeRate
                  }{" "}
                  over{" "}
                  {props.originalRatecardData?.ratecardValues.discountFrequency}{" "}
                  days then discount equals to{" "}
                  {props.originalRatecardData?.ratecardValues
                    .discountUnitType === "value"
                    ? `$`
                    : ``}{" "}
                  {props.originalRatecardData?.ratecardValues.discountValue}
                  {props.originalRatecardData?.ratecardValues
                    .discountUnitType === "percentage"
                    ? `%`
                    : ``}
                </>
              </Typography>
            ) : null}
          </Box>
        </Grid>
      ) : null}
    </>
  );
};

export default CiDiscountPremium;
